/**
 * AdminBroadcastBanner — Surfaces active admin broadcasts (type='announcement')
 * on the dashboard. Each broadcast is dismissible once and persisted in
 * admin_broadcast_acks so it disappears for that user on every device.
 */
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, X, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/utils/logger";

interface Broadcast {
  id: string;
  title: string;
  body: string | null;
  description: string | null;
  cta_label: string | null;
  cta_url: string | null;
  emirate: string | null;
  type: string;
  is_active: boolean;
  ends_at: string | null;
  created_at: string;
}

export function AdminBroadcastBanner() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: broadcasts = [] } = useQuery({
    queryKey: ["admin-broadcasts-active"],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("admin_city_tasks")
        .select("id,title,body,description,cta_label,cta_url,emirate,type,is_active,ends_at,created_at")
        .eq("type", "announcement")
        .eq("is_active", true)
        .or(`ends_at.is.null,ends_at.gte.${nowIso}`)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) {
        logger.error("Load broadcasts failed", error);
        return [];
      }
      return (data ?? []) as Broadcast[];
    },
  });

  const { data: ackedIds = [] } = useQuery({
    queryKey: ["admin-broadcast-acks", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("admin_broadcast_acks")
        .select("broadcast_id")
        .eq("user_id", user!.id);
      return (data ?? []).map((r: any) => r.broadcast_id as string);
    },
  });

  const ackedSet = useMemo(() => new Set(ackedIds), [ackedIds]);
  const visible = broadcasts.filter((b) => !ackedSet.has(b.id));

  if (!user?.id || visible.length === 0) return null;

  const dismiss = async (broadcastId: string) => {
    // Optimistic update
    qc.setQueryData<string[]>(
      ["admin-broadcast-acks", user.id],
      (old) => [...(old ?? []), broadcastId],
    );
    const { error } = await supabase.from("admin_broadcast_acks").insert({
      broadcast_id: broadcastId,
      user_id: user.id,
    });
    if (error) {
      logger.error("Dismiss broadcast failed", error);
      qc.invalidateQueries({ queryKey: ["admin-broadcast-acks", user.id] });
    }
  };

  return (
    <div className="space-y-2">
      {visible.map((b) => {
        const body = b.body ?? b.description;
        return (
          <Card
            key={b.id}
            className="border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent"
          >
            <CardContent className="p-4 flex items-start gap-3">
              <div className="rounded-full bg-primary/15 p-2 shrink-0">
                <Megaphone className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold leading-tight">{b.title}</h3>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => dismiss(b.id)}
                    className="h-7 w-7 -mt-1 -mr-1 shrink-0"
                    aria-label="Dismiss"
                    style={{ touchAction: "manipulation" }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {body && (
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                    {body}
                  </p>
                )}
                {b.cta_url && (
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="mt-2 gap-1"
                  >
                    <a
                      href={b.cta_url}
                      target={b.cta_url.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                    >
                      {b.cta_label || "Learn more"}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default AdminBroadcastBanner;
